import { useEffect, useRef } from "react";

const MOBILE_AUTH_MAX_WIDTH = 767;

function isMobileAuthViewport() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${MOBILE_AUTH_MAX_WIDTH}px)`).matches
  );
}

export default function useAuthFormKeyboardScroll() {
  const panelRef = useRef(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !isMobileAuthViewport()) return undefined;

    const viewport = window.visualViewport;

    const syncKeyboardInset = () => {
      if (!viewport) {
        panel.style.removeProperty("--auth-keyboard-inset");
        return;
      }

      const keyboardInset = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop
      );
      panel.style.setProperty("--auth-keyboard-inset", `${keyboardInset}px`);
    };

    const scrollFocusedFieldIntoView = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches("input, textarea, select")) return;

      syncKeyboardInset();

      window.setTimeout(() => {
        const form = target.closest("form");
        const submitButton = form?.querySelector('[type="submit"]');
        const isPasswordField =
          target instanceof HTMLInputElement &&
          (target.type === "password" || target.name === "confirmPassword");

        if (isPasswordField && submitButton instanceof HTMLElement) {
          submitButton.scrollIntoView({ block: "nearest", inline: "nearest" });
          return;
        }

        target.scrollIntoView({ block: "center", inline: "nearest" });
      }, 320);
    };

    syncKeyboardInset();
    panel.addEventListener("focusin", scrollFocusedFieldIntoView);

    if (viewport) {
      viewport.addEventListener("resize", syncKeyboardInset);
      viewport.addEventListener("scroll", syncKeyboardInset);
    }

    return () => {
      panel.removeEventListener("focusin", scrollFocusedFieldIntoView);
      if (viewport) {
        viewport.removeEventListener("resize", syncKeyboardInset);
        viewport.removeEventListener("scroll", syncKeyboardInset);
      }
      panel.style.removeProperty("--auth-keyboard-inset");
    };
  }, []);

  return panelRef;
}
